<template>
  <v-container class="mt-16">
    <v-row class="chat-container">
      <v-col>
        <v-card flat>
          <div class="pa-4">
            <h2 class="devcentre-intro">{{ projectData.name }}</h2>
          </div>
          <v-card-text class="devcentre-subintro">
            <p class="mb-2">{{ projectData.description }}</p>

            <v-progress-linear
              :model-value="submissionProgress"
              height="30"
              color="primary"
              rounded
              striped
            ></v-progress-linear>

            <v-spacer class="my-4"></v-spacer>

            <div v-if="limitReached">
              <v-card class="text-center pa-6" elevation="0">
                <v-card-title class="text-h4 text-info">
                  This Project is now closed
                </v-card-title>
                <v-card-text>
                  <div class="text-h2 mb-4">🙏</div>
                  <p class="text-h6">Thank you for your interest!</p>
                  <p class="text-subtitle-1 text-grey-darken-1">
                    This research project has reached capacity and is no longer
                    accepting responses.
                  </p>
                </v-card-text>
              </v-card>
            </div>
            <div v-else-if="!interviewComplete">
              <v-progress-circular
                v-if="!interviewSessionId"
                indeterminate
                color="primary"
              />
              <AIAssistant
                v-else
                @saved="interviewSaved($event)"
                @message_count="updateProgress($event)"
                @last_text="finalWord = $event"
                prompt_text="Reply here"
                :key="interviewSessionId"
                :session="interviewSessionId"
                :tenant_id="tenantId"
                assistant_image="assets/relaxed_character.jpg"
              />
            </div>
            <div v-else>
              <v-card class="text-center pa-6" elevation="0">
                <v-card-title class="text-h4 text-success">
                  Interview Complete!
                </v-card-title>
                <v-card-text>
                  <div class="text-h2 mb-4">🎉</div>
                  <p class="text-h6">Thank you for completing the interview!</p>
                  <p class="text-subtitle-1 text-grey-darken-1">
                    Your responses have been recorded.
                  </p>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AIAssistant from "@/components/AIAssistant";
import APIService from "@/services/APIService";

export default {
  name: "InterView",
  components: {
    AIAssistant,
  },

  data: () => ({
    projectData: {
      _id: "",
      name: "",
      description: "",
      assistant_id: "",
    },
    interviewSessionId: null,
    currentSubmissions: 0,
    targetSubmissions: 17,
    loading: false,
    limitReached: false,
    finalWord: "",
    interviewComplete: false,
  }),

  computed: {
    tenantId() {
      return this.$route.params.tenantId;
    },
    projectId() {
      return this.$route.params.id;
    },
    submissionProgress() {
      if (this.interviewComplete) return 100;
      return (this.currentSubmissions / this.targetSubmissions) * 100;
    },
  },

  methods: {
    async startInterview() {
      try {
        // Create interview record — automation rule will create the session and write back session_id
        const res = await APIService.createPublicRecord(
          this.tenantId,
          "di_interviews",
          {
            project_id: this.projectId,
            state: "started",
            started_at: new Date().toISOString(),
          }
        );
        const interviewId = res.data._id;

        // Poll the interview_sessions public view until session_id is populated
        for (let i = 0; i < 20; i++) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const viewRes = await APIService.getPublicViewData(
            this.tenantId,
            "interview_sessions"
          );
          const record = (viewRes.data.records || []).find(
            (r) => r._id === interviewId
          );
          if (record?.session_id) {
            this.interviewSessionId = record.session_id;
            return;
          }
        }
        console.error("Timed out waiting for interview session_id");
      } catch (error) {
        console.error("Error starting interview:", error);
      }
    },

    async interviewSaved() {
      this.interviewComplete = true;
      this.currentSubmissions = this.targetSubmissions;
    },

    async updateProgress(no) {
      this.currentSubmissions = no;
      if (this.targetSubmissions === no) {
        this.interviewComplete = true;
      }
    },
  },

  async created() {
    try {
      const response = await APIService.getPublicViewData(
        this.tenantId,
        "live_projects"
      );
      const project = (response.data.records || []).find(
        (p) => p._id === this.projectId
      );
      if (project) {
        this.projectData = project;
        await this.startInterview();
      } else {
        this.limitReached = true;
      }
    } catch (error) {
      if (error.response?.status === 403) {
        this.limitReached = true;
      } else {
        console.error("Error loading project:", error);
      }
    }
  },
};
</script>

<style scoped>
.chat-container {
  position: relative;
  background: white;
  border: none !important;
  overflow: hidden;
}

.chat-container::before {
  content: "";
  position: absolute;
  inset: 0;
  padding: 2px;
  background: linear-gradient(135deg, #2ab3b8, #4fd1d6);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}
</style>
